import type {
  Announcements,
  ScreenReaderInstructions,
  UniqueIdentifier,
} from "@dnd-kit/core";

import i18n from "@/i18n";

/**
 * What a screen reader hears while a vertical list is reordered by drag, for
 * every sortable list in the app (`SortableList` and the builder's question
 * list). dnd-kit's defaults are English and read the items' internal ids
 * ("Draggable item q_unlikely_reason was dropped over droppable area
 * q_value_credibility", E-logic-13), so the list says where the item is
 * instead: its position among `ids`, the list's items in their order.
 */
type SortableAnnouncementKey =
  | "pickedUp"
  | "over"
  | "outside"
  | "dropped"
  | "droppedOutside"
  | "cancelled";

export function sortableAccessibility(ids: ReadonlyArray<UniqueIdentifier>): {
  announcements: Announcements;
  screenReaderInstructions: ScreenReaderInstructions;
} {
  const total = ids.length;
  const positionOf = (id: UniqueIdentifier): number | null => {
    const index = ids.indexOf(id);
    return index === -1 ? null : index + 1;
  };
  const say = (key: SortableAnnouncementKey, values: Record<string, number>) =>
    i18n.t(`sortable.${key}`, { ns: "common", total, ...values });
  return {
    announcements: {
      onDragStart({ active }) {
        const position = positionOf(active.id);
        return position === null ? undefined : say("pickedUp", { position });
      },
      onDragOver({ active, over }) {
        const from = positionOf(active.id);
        if (from === null) {
          return undefined;
        }
        const to = over ? positionOf(over.id) : null;
        return to === null ? say("outside", { from }) : say("over", { from, to });
      },
      onDragEnd({ active, over }) {
        const from = positionOf(active.id);
        if (from === null) {
          return undefined;
        }
        const to = over ? positionOf(over.id) : null;
        return to === null
          ? say("droppedOutside", { from })
          : say("dropped", { from, to });
      },
      onDragCancel({ active }) {
        const position = positionOf(active.id);
        return position === null ? undefined : say("cancelled", { position });
      },
    },
    screenReaderInstructions: {
      draggable: i18n.t("sortable.instructions", { ns: "common" }),
    },
  };
}
